import { ReactNode } from 'react';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { isNonZero } from '@features/transactions/helpers/paymentDetails';

interface PaymentSummaryStatsRowProps {
  totalPaid: number;
  totalDiscount: number;
  remainingBalance: number;
  isDefaultCurrency: boolean;
}

const amountClass = (isDefaultCurrency: boolean, color: string) =>
  `${
    isDefaultCurrency ? 'text-md !font-normal' : ''
  } text-sm font-semibold ${color}`;

const PaymentSummaryStatsRow = ({
  totalPaid,
  totalDiscount,
  remainingBalance,
  isDefaultCurrency
}: PaymentSummaryStatsRowProps) => {
  const gapClass = isDefaultCurrency ? 'gap-0.25' : 'gap-0.5';

  const cells: { key: string; label: string; content: ReactNode }[] = [];

  if (isNonZero(totalPaid)) {
    cells.push({
      key: 'paid',
      label: 'Total paid',
      content: (
        <CurrencyAmount
          value={totalPaid}
          className={`justify-center ${gapClass}`}
          symbolClass={amountClass(isDefaultCurrency, 'text-cofeeGreen7')}
          valueClass={amountClass(isDefaultCurrency, 'text-cofeeGreen7')}
        />
      )
    });
  }

  if (totalDiscount > 0) {
    cells.push({
      key: 'discount',
      label: 'Total discount',
      content: (
        <div className="flex items-baseline justify-center gap-0.5">
          <span className="text-xs font-semibold text-cofeeTeal5">-</span>
          <CurrencyAmount
            value={totalDiscount}
            className={`justify-center ${gapClass}`}
            symbolClass={amountClass(isDefaultCurrency, 'text-cofeeTeal5')}
            valueClass={amountClass(isDefaultCurrency, 'text-cofeeTeal5')}
          />
        </div>
      )
    });
  }

  if (isNonZero(remainingBalance)) {
    cells.push({
      key: 'remaining',
      label: 'Remaining',
      content: (
        <CurrencyAmount
          value={remainingBalance}
          className={`justify-center ${gapClass}`}
          symbolClass={amountClass(isDefaultCurrency, 'text-cofeeOrange6')}
          valueClass={amountClass(isDefaultCurrency, 'text-cofeeOrange6')}
        />
      )
    });
  }

  if (cells.length <= 1) return null;

  return (
    <div className="mt-4 w-full border-t border-cofeeGreyBlue4 pt-2">
      <div className="flex w-full divide-x divide-cofeeGreyBlue4 bg-white">
        {cells.map(({ key, label, content }) => (
          <div key={key} className="flex min-w-0 flex-1 flex-col items-center">
            <span className="text-center text-sm font-normal leading-tight text-cofeeNeutral8">
              {label}
            </span>
            {content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentSummaryStatsRow;
